/**
 * ZicassoBestPractices — Timeline oficial + reglas de oro + templates + CRUD de
 * ideas de automatización (agrupadas por estado).
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ListChecks, Lightbulb, ScrollText, Sparkles, Plus, Trash2, X,
  Check, ArrowUpRight, Pencil,
} from "lucide-react";

import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { sanitizeHtml, htmlToText } from "@/lib/html";

const PRIORITY_COLORS = {
  high: "border-[#C45A45]/30 bg-[#FCEEEA] text-[#803D26]",
  medium: "border-[#C28F5B]/30 bg-[#F1E9DA] text-[#6B4F20]",
  low: "border-black/10 bg-secondary text-foreground",
};
const STATUS_META = {
  idea: { label: "Idea", color: "bg-secondary text-foreground border-black/10" },
  approved: { label: "Aprobada", color: "bg-[#E5E8F2] text-[#34406B] border-[#34406B]/20" },
  in_progress: { label: "En curso", color: "bg-[#F1E9DA] text-[#6B4F20] border-[#6B4F20]/20" },
  implemented: { label: "Implementada", color: "bg-[#E2EBE5] text-[#244532] border-[#244532]/20" },
  rejected: { label: "Descartada", color: "bg-secondary text-muted-foreground border-black/10" },
};

function TimelineSection({ timeline }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold">
          <ListChecks className="h-5 w-5 text-primary" /> Timeline oficial de Zicasso (10 pasos)
        </h3>
        <div className="space-y-2" data-testid="bp-timeline">
          {timeline.map((step) => (
            <div key={step.step} className={`flex gap-3 rounded-lg border p-3 ${PRIORITY_COLORS[step.priority]}`} data-testid={`bp-step-${step.step}`}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-card text-lg font-bold shadow-sm">
                {step.step}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{step.day}</span>
                  <Badge variant="outline" className="h-5 text-[10px]">{step.channel === "call" ? "Llamada" : "Mensaje"}</Badge>
                  <Badge variant="outline" className={`h-5 text-[10px] ${PRIORITY_COLORS[step.priority]}`}>Prioridad {step.priority}</Badge>
                </div>
                <div className="mt-1 text-sm font-semibold">{step.action}</div>
                <div className="mt-1 text-xs leading-relaxed opacity-80">{step.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RulesSection({ rules, onProposeAutomation }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold">
            <Lightbulb className="h-5 w-5 text-[#C28F5B]" /> Reglas de oro ({rules.length})
          </h3>
          <p className="hidden text-[11px] text-muted-foreground sm:block">Click en una regla → propón una automatización</p>
        </div>
        <div className="grid gap-2 md:grid-cols-2" data-testid="bp-rules">
          {rules.map((rule, i) => (
            <button
              key={rule.title}
              onClick={() => onProposeAutomation(rule)}
              className="rounded-lg border-l-4 border-[#C28F5B] bg-[#F1E9DA]/40 p-3 text-left transition-all hover:bg-[#F1E9DA] hover:shadow-sm"
              data-testid={`bp-rule-${i}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">{rule.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{rule.text}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-[#C28F5B]" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TemplatesSection({ templates }) {
  const [selected, setSelected] = useState(null);
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold">
          <ScrollText className="h-5 w-5 text-primary" /> Templates disponibles ({templates.length})
        </h3>
        <div className="grid gap-2 md:grid-cols-3" data-testid="bp-templates">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              className={`rounded-lg border p-3 text-left transition-colors hover:bg-accent ${selected?.id === t.id ? "border-primary bg-accent" : "border-black/5"}`}
              data-testid={`bp-template-${t.id}`}
            >
              <div className="text-sm font-semibold">{t.title}</div>
              <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">{htmlToText(t.body).substring(0, 100)}…</div>
            </button>
          ))}
        </div>
        {selected && (
          <div className="mt-4 rounded-lg border-2 border-primary/20 bg-secondary/30 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-base font-bold">{selected.title}</h4>
              <Button size="sm" variant="ghost" onClick={() => setSelected(null)}><X className="h-3 w-3" /></Button>
            </div>
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_p]:my-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(selected.body) }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const EMPTY_IDEA = { title: "", description: "", priority: "medium", status: "idea", linked_rule: null };

function AutomationIdeaDialog({ open, onClose, initial, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_IDEA);
  React.useEffect(() => { setForm(initial || EMPTY_IDEA); }, [initial, open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="automation-idea-dialog">
        <DialogTitle>{initial?.id ? "Editar idea" : "Nueva idea de automatización"}</DialogTitle>
        <DialogDescription>Describe la automatización que quieres proponer.</DialogDescription>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold">Título</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Auto-marcar INACTIVE tras 4 meses" data-testid="idea-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold">Descripción</label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Disparadores, acciones, condiciones…" data-testid="idea-description-textarea" />
          </div>
          {form.linked_rule && (
            <div className="rounded-lg border-l-2 border-[#C28F5B] bg-[#F1E9DA] p-2 text-xs">
              <span className="font-semibold text-[#6B4F20]">Basada en regla:</span> {form.linked_rule}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold">Prioridad</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="idea-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Media</SelectItem>
                  <SelectItem value="low">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="idea-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="idea">Idea</SelectItem>
                  <SelectItem value="approved">Aprobada</SelectItem>
                  <SelectItem value="in_progress">En curso</SelectItem>
                  <SelectItem value="implemented">Implementada</SelectItem>
                  <SelectItem value="rejected">Descartada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(form)} disabled={!form.title.trim()} data-testid="idea-save-button">
            <Check className="mr-1 h-3 w-3" /> Guardar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IdeaCard({ idea, onEdit, onDelete }) {
  const status = STATUS_META[idea.status] || STATUS_META.idea;
  return (
    <div className={`rounded-lg border border-black/5 bg-card p-3 transition-shadow hover:shadow-sm ${idea.status === "implemented" ? "opacity-70" : ""}`} data-testid={`idea-card-${idea.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{idea.title}</span>
            <Badge className={`h-4 border text-[9px] ${status.color}`}>{status.label}</Badge>
            <Badge variant="outline" className={`h-4 text-[9px] ${PRIORITY_COLORS[idea.priority]}`}>{idea.priority}</Badge>
          </div>
          {idea.description && <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{idea.description}</p>}
          {idea.linked_rule && <p className="mt-1 text-[10px] text-[#6B4F20]">Basada en: <em>{idea.linked_rule}</em></p>}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant="ghost" onClick={() => onEdit(idea)} data-testid={`edit-idea-${idea.id}`}><Pencil className="h-3 w-3" /></Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(idea.id)} data-testid={`delete-idea-${idea.id}`}><Trash2 className="h-3 w-3 text-[#C45A45]" /></Button>
        </div>
      </div>
    </div>
  );
}

function IdeasSection({ dialogState }) {
  const qc = useQueryClient();
  const { dialogOpen, setDialogOpen, editing, setEditing } = dialogState;

  const ideas = useQuery({
    queryKey: ["zicasso-automation-ideas"],
    queryFn: async () => (await api.get("/zicasso/automation-ideas")).data,
  });

  const saveMut = useMutation({
    mutationFn: (form) =>
      form.id
        ? api.put(`/zicasso/automation-ideas/${form.id}`, form)
        : api.post("/zicasso/automation-ideas", form),
    onSuccess: () => {
      toast.success("Idea guardada");
      qc.invalidateQueries({ queryKey: ["zicasso-automation-ideas"] });
      setDialogOpen(false); setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error guardando"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/zicasso/automation-ideas/${id}`),
    onSuccess: () => {
      toast.success("Idea eliminada");
      qc.invalidateQueries({ queryKey: ["zicasso-automation-ideas"] });
    },
  });

  const items = ideas.data?.items || [];
  const byStatus = ["idea", "approved", "in_progress", "implemented", "rejected"]
    .map((k) => ({ key: k, meta: STATUS_META[k], items: items.filter((i) => i.status === k) }))
    .filter((g) => g.items.length > 0);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-heading text-lg font-semibold">
              <Sparkles className="h-5 w-5 text-primary" /> Ideas de automatización
            </h3>
            <p className="text-[11px] text-muted-foreground">Anota propuestas para la fase 2 de IA & automatizaciones.</p>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-idea-button">
            <Plus className="mr-1 h-3 w-3" /> Nueva idea
          </Button>
        </div>

        {ideas.isLoading ? (
          <Skeleton className="h-32" />
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            <Lightbulb className="mx-auto mb-2 h-8 w-8 text-[#C28F5B]" />
            <p>Aún no hay ideas propuestas.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {byStatus.map((g) => (
              <div key={g.key}>
                <div className={`mb-2 inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${g.meta.color}`}>
                  {g.meta.label} ({g.items.length})
                </div>
                <div className="space-y-2" data-testid={`ideas-group-${g.key}`}>
                  {g.items.map((idea) => (
                    <IdeaCard
                      key={idea.id}
                      idea={idea}
                      onEdit={(x) => { setEditing(x); setDialogOpen(true); }}
                      onDelete={(id) => window.confirm("¿Eliminar esta idea?") && deleteMut.mutate(id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <AutomationIdeaDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          initial={editing}
          onSave={(form) => saveMut.mutate({ ...form, id: editing?.id })}
        />
      </CardContent>
    </Card>
  );
}

export default function ZicassoBestPractices() {
  const bp = useQuery({
    queryKey: ["zicasso-best-practices"],
    queryFn: async () => (await api.get("/zicasso/best-practices")).data,
    staleTime: 30 * 60_000,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleProposeFromRule = (rule) => {
    setEditing({
      title: `Automatizar: ${rule.title}`,
      description: `Regla Zicasso: "${rule.title}"\n\n${rule.text}\n\n→ Propuesta concreta de automatización:`,
      priority: "medium", status: "idea", linked_rule: rule.title,
    });
    setDialogOpen(true);
  };

  if (bp.isLoading || !bp.data) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-4 px-6 py-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-4 px-6 py-6" data-testid="zicasso-best-practices">
      <div className="rounded-xl border border-[#C28F5B]/20 bg-gradient-to-r from-[#F1E9DA] to-[#F4F2EC] p-6">
        <h1 className="mb-2 flex items-center gap-2 font-heading text-2xl font-semibold tracking-tight">
          <Lightbulb className="h-6 w-6 text-[#C28F5B]" /> Reglas & Best Practices de Zicasso
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Las normas oficiales de Zicasso para gestionar leads. Cuando veas oportunidades de
          automatización, haz click en una regla o pulsa &quot;Nueva idea&quot; para anotarlas.
        </p>
      </div>

      <TimelineSection timeline={bp.data.timeline} />
      <RulesSection rules={bp.data.rules} onProposeAutomation={handleProposeFromRule} />
      <TemplatesSection templates={bp.data.templates} />
      <IdeasSection dialogState={{ dialogOpen, setDialogOpen, editing, setEditing }} />

      <div className="rounded-lg border border-dashed p-3 text-center">
        <p className="text-[11px] text-muted-foreground">
          Fuente:{" "}
          <a href={bp.data.sources[0]?.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
            {bp.data.sources[0]?.label}
          </a>
        </p>
      </div>
    </div>
  );
}
